import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUserQuery, useUpdateUserVerificationMutation, useGetAdsQuery, useAddWalletBalanceMutation, useGetUserViolationsQuery, useGiveUserViolationMutation, useGetUserReportsQuery, useToggleUserStatusMutation, useReviewUserAppealMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import { formatDateOnly, formatDateTime, getStatusColor, formatCurrency } from '../../../utils/helpers';
import { showSuccess, showError } from '../../../utils/toast';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { useLanguage } from '../../../context/LanguageContext';


const ReportCard: React.FC<{ report: any; onOpenAd?: (adId: number) => void }> = ({ report: r, onOpenAd }) => {
  const { t } = useLanguage();
  return (
  <div className="border border-gray-200 rounded p-5">
    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
      <div className="d-flex align-items-center">
        <div className="symbol symbol-45px me-3">
          {r.reporter?.picture ? (
            <Image src={r.reporter.picture} alt={r.reporter?.name} type="avatar" className="w-45px h-45px rounded-circle object-fit-cover" />
          ) : (
            <span className="symbol-label bg-light-danger text-danger fw-bold">
              {(r.reporter?.name || 'U').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="d-flex flex-column">
          <span className="fw-bold text-gray-800 fs-6">{r.reporter?.name || t('Unknown User')}</span>
          {r.reporter?.email && <span className="text-muted fs-7">{r.reporter.email}</span>}
        </div>
      </div>
      <div className="d-flex flex-column align-items-end gap-1">
        <span className={`badge fs-8 fw-bold text-capitalize ${
          r.status === 'resolved' ? 'badge-light-success' :
          r.status === 'reviewed' ? 'badge-light-info' :
          r.status === 'dismissed' ? 'badge-light-secondary text-dark' :
          'badge-light-warning'
        }`}>
          {r.status ? t(r.status) : t('pending')}
        </span>
        <span className="text-muted fs-8">{formatDateTime(r.created_at)}</span>
      </div>
    </div>

    {r.ad && (
      <div
        className={`d-flex align-items-center bg-light-primary rounded p-3 mb-4 ${onOpenAd ? 'cursor-pointer' : ''}`}
        onClick={onOpenAd ? () => onOpenAd(r.ad.id) : undefined}
      >
        <div className="symbol symbol-50px me-3">
          {r.ad.image ? (
            <Image src={r.ad.image} alt={r.ad.title} className="w-50px h-50px rounded object-fit-cover" />
          ) : (
            <span className="symbol-label bg-light-primary text-primary">
              <i className="bi bi-megaphone-fill fs-4"></i>
            </span>
          )}
        </div>
        <div className="d-flex flex-column">
          <span className="text-muted fw-semibold fs-8">{t('Reported Ad')}</span>
          <span className="fw-bold text-gray-800 fs-6">{r.ad.title || `${t('Ad')} #${r.ad.id}`}</span>
          <span className="text-muted fs-8">{t('Owner')}: {r.ad.owner?.name || t('Unknown')}</span>
        </div>
      </div>
    )}

    <div className="mb-3">
      <span className="text-muted fw-semibold fs-7 d-block mb-1">{t('Reason')}</span>
      <span className="fw-bold text-gray-800 fs-6">{r.reason || '—'}</span>
    </div>

    {r.details && (
      <div className="mb-3">
        <span className="text-muted fw-semibold fs-7 d-block mb-1">{t('Details')}</span>
        <span className="text-gray-700 fs-6">{r.details}</span>
      </div>
    )}

    {Array.isArray(r.images) && r.images.length > 0 && (
      <div>
        <span className="text-muted fw-semibold fs-7 d-block mb-2">{t('Attachments')}</span>
        <div className="d-flex flex-wrap gap-2">
          {r.images.map((img: string, idx: number) => (
            <a key={idx} href={img} target="_blank" rel="noreferrer" className="d-block">
              <Image src={img} alt={`${t('Report image')} ${idx + 1}`} className="w-80px h-80px rounded object-fit-cover border border-gray-200" />
            </a>
          ))}
        </div>
      </div>
    )}
  </div>
  );
};

const ReportsSection: React.FC<{
  title: string;
  emptyText: string;
  reports: any[] | undefined;
  isLoading: boolean;
  onOpenAd?: (adId: number) => void;
}> = ({ title, emptyText, reports, isLoading, onOpenAd }) => {
  const { t } = useLanguage();
  return (
  <div className="card mb-5 mb-xl-10">
    <div className="card-header cursor-pointer">
      <div className="card-title m-0">
        <h3 className="fw-bold m-0">{title}</h3>
      </div>
      {reports && reports.length > 0 && (
        <div className="card-toolbar">
          <span className="badge badge-light-danger fs-7 fw-bold">
            {reports.length} {reports.length > 1 ? t('reports') : t('report')}
          </span>
        </div>
      )}
    </div>
    <div className="card-body p-9">
      {isLoading ? (
        <PageLoader />
      ) : reports && reports.length > 0 ? (
        <div className="d-flex flex-column gap-5">
          {reports.map((r: any) => (
            <ReportCard key={r.id} report={r} onOpenAd={onOpenAd} />
          ))}
        </div>
      ) : (
        <div className="text-center text-muted py-10">
          <i className="bi bi-flag fs-3x text-gray-300 d-block mb-3"></i>
          {emptyText}
        </div>
      )}
    </div>
  </div>
  );
};

const AdminUserView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t, lang } = useLanguage();
  const [activeTab, setActiveTab] = useState<'info' | 'ads' | 'violations' | 'reports'>('info');
  const [adsPage, setAdsPage] = useState(1);

  // Verification modal
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'approve' | 'reject' | null>(null);
  const [node, setNode] = useState('');
  const [documents, setDocuments] = useState<File[]>([]);

  // Wallet modal
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletType, setWalletType] = useState<'add' | 'deduct'>('add');
  const [walletAmount, setWalletAmount] = useState('');
  const [walletNote, setWalletNote] = useState('');

  // Account deactivation modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');

  // Appeal review modal
  const [appealModalOpen, setAppealModalOpen] = useState(false);
  const [appealAction, setAppealAction] = useState<'approve' | 'reject'>('approve');
  const [appealNote, setAppealNote] = useState('');

  // Violation modal
  const [violationModalOpen, setViolationModalOpen] = useState(false);
  const [violationAdId, setViolationAdId] = useState('');
  const [violationType, setViolationType] = useState('Policy Non-Compliance');
  const [violationStatus, setViolationStatus] = useState('Under Review');
  const [violationNote, setViolationNote] = useState('');

  const { data, isLoading, error } = useGetUserQuery(id!);
  const { data: adsData, isLoading: isAdsLoading } = useGetAdsQuery({ user_id: id, page: adsPage }, { skip: !id || (activeTab !== 'ads' && !violationModalOpen) });
  const { data: violationsData, isLoading: isViolationsLoading } = useGetUserViolationsQuery(id!, { skip: !id || activeTab !== 'violations' });
  const { data: reportsData, isLoading: isReportsLoading } = useGetUserReportsQuery(id!, { skip: !id || activeTab !== 'reports' });
  const [giveUserViolation, { isLoading: isViolationSubmitting }] = useGiveUserViolationMutation();
  const [updateUserVerification, { isLoading: isUpdating }] = useUpdateUserVerificationMutation();
  const [addWalletBalance, { isLoading: isWalletLoading }] = useAddWalletBalanceMutation();
  const [toggleUserStatus, { isLoading: isStatusUpdating }] = useToggleUserStatusMutation();
  const [reviewUserAppeal, { isLoading: isAppealUpdating }] = useReviewUserAppealMutation();

  const openWalletModal = (type: 'add' | 'deduct') => {
    setWalletType(type);
    setWalletAmount('');
    setWalletNote('');
    setWalletModalOpen(true);
  };

  const handleWalletSubmit = async () => {
    const amount = parseFloat(walletAmount);
    if (!amount || amount <= 0) return showError(t('Please enter a valid amount.'));
    try {
      const result = await addWalletBalance({
        id: user.id,
        amount,
        type: walletType,
        note: walletNote || undefined,
      }).unwrap();
      showSuccess(result.message || t('Wallet balance updated!'));
      setWalletModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update wallet.'));
    }
  };

  const openStatusModal = () => {
    setDeactivationReason('');
    setStatusModalOpen(true);
  };

  const handleStatusSubmit = async () => {
    const isDeactivating = user.accStatus === 1;
    const reason = deactivationReason.trim();

    if (isDeactivating && !reason) {
      return showError(t('Please enter a reason for deactivation.'));
    }

    try {
      const result = await toggleUserStatus({
        id: user.id,
        status: isDeactivating ? 0 : 1,
        reason: isDeactivating ? reason : undefined,
      }).unwrap();
      showSuccess(result.message || (isDeactivating ? t('Account deactivated.') : t('Account reactivated.')));
      setStatusModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update account status.'));
    }
  };

  const openAppealModal = (action: 'approve' | 'reject') => {
    setAppealAction(action);
    setAppealNote('');
    setAppealModalOpen(true);
  };

  const handleAppealSubmit = async () => {
    const note = appealNote.trim();

    if (appealAction === 'reject' && !note) {
      return showError(t('Please enter a reason for rejecting this appeal.'));
    }

    try {
      const result = await reviewUserAppeal({
        id: user.id,
        action: appealAction,
        review_note: note || undefined,
      }).unwrap();
      showSuccess(result.message || (appealAction === 'approve' ? t('Appeal approved.') : t('Appeal rejected.')));
      setAppealModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to review appeal.'));
    }
  };

  const openViolationModal = () => {
    setViolationAdId('');
    setViolationType('Policy Non-Compliance');
    setViolationStatus('Under Review');
    setViolationNote('');
    setViolationModalOpen(true);
  };

  const handleViolationSubmit = async () => {
    if (!violationAdId) return showError(t('Please select an ad.'));
    try {
      const result = await giveUserViolation({
        userId: user.id,
        ad_id: violationAdId,
        violation_type: violationType,
        status: violationStatus,
        note: violationNote || undefined,
      }).unwrap();
      showSuccess(result.message || t('Violation issued successfully!'));
      setViolationModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to issue violation.'));
    }
  };

  const openVerificationModal = (status: 'approve' | 'reject') => {
    setPendingStatus(status);
    setNode('');
    setDocuments([]);
    setModalOpen(true);
  };

  const handleVerificationSubmit = async () => {
    if (!pendingStatus) return;
    try {
      const result = await updateUserVerification({
        id: user.id,
        status: pendingStatus,
        node,
        documents,
      }).unwrap();
      showSuccess(result.message || (pendingStatus === 'approve' ? t('User verification approved successfully!') : t('User verification rejected successfully!')));
      setModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update verification status.'));
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !data?.data) {
    return <div className="alert alert-danger m-5">{t('User not found')}</div>;
  }

  const user = data.data;
  const deactivation = user.deactivation || {};
  const appealStatus = deactivation.status;
  const hasAppeal = ['appealed', 'appeal_approved', 'appeal_rejected'].includes(appealStatus);
  const isAppealPending = appealStatus === 'appealed' ;
  const appealImages: string[] = Array.isArray(deactivation.appeal_images) ? deactivation.appeal_images : [];

  return (
    <>
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('User Details')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Home')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/users'); }} className="text-muted text-hover-primary">{t('Users')}</a>
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              {user.accStatus === 1 ? (
                <button className="btn btn-light-danger" onClick={openStatusModal}>
                  <i className="fas fa-user-slash me-2"></i>{t('Deactivate Account')}
                </button>
              ) : (
                <button className="btn btn-light-success" onClick={openStatusModal}>
                  <i className="fas fa-user-check me-2"></i>{t('Reactivate Account')}
                </button>
              )}
              <button className="btn btn-primary" onClick={() => navigate(`/admin/users/${id}/edit`)}>
                {t('Edit User')}
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">

            {/* Deactivation banner */}
            {user.accStatus !== 1 && (
              <div className="alert alert-danger d-flex align-items-start flex-wrap gap-3 mb-5">
                <i className="fas fa-user-slash fs-2x text-danger"></i>
                <div className="flex-grow-1">
                  <h4 className="mb-1 text-danger">{t('Account Deactivated')}</h4>
                  <div className="text-gray-700">
                    {t('This user cannot use the platform.')}
                    {user.deactivation?.reason && <> {t('Reason')}: <strong>{user.deactivation?.reason}</strong>.</>}
                  </div>
                  <div className="text-muted fs-7 mt-1">
                    {user.deactivation?.deactivated_at && <>{t('Deactivated on')} {formatDateTime(user.deactivation?.deactivated_at)}</>}
                    {user.deactivation?.deactivated_by && <> {t('by')} {user.deactivation?.deactivated_by}</>}
                  </div>
                </div>
                <button className="btn btn-sm btn-success" onClick={openStatusModal}>
                  <i className="fas fa-user-check me-2"></i>{t('Reactivate')}
                </button>
              </div>
            )}

            {/* Appeal awaiting review */}
            {isAppealPending && (
              <div className="alert alert-warning d-flex align-items-start flex-wrap gap-3 mb-5">
                <i className="fas fa-gavel fs-2x text-warning"></i>
                <div className="flex-grow-1">
                  <h4 className="mb-1 text-warning">{t('Appeal Awaiting Review')}</h4>
                  <div className="text-gray-700">
                    {t('This user has appealed their deactivation. Review the details below and approve or reject the appeal.')}
                  </div>
                  {deactivation.appealed_at && (
                    <div className="text-muted fs-7 mt-1">{t('Submitted on')} {formatDateTime(deactivation.appealed_at)}</div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-success" onClick={() => openAppealModal('approve')}>
                    <i className="fas fa-check me-2"></i>{t('Approve')}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => openAppealModal('reject')}>
                    <i className="fas fa-times me-2"></i>{t('Reject')}
                  </button>
                </div>
              </div>
            )}

            {/* Appeal details */}
            {hasAppeal && isAppealPending && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">{t('User Appeal')}</h3>
                  </div>
                  <div className="card-toolbar">
                    <span className={`badge fs-7 fw-bold ${
                      appealStatus === 'appeal_approved' ? 'badge-light-success' :
                      appealStatus === 'appeal_rejected' ? 'badge-light-danger' :
                      'badge-light-warning'
                    }`}>
                      {appealStatus === 'appeal_approved' ? t('Approved') :
                       appealStatus === 'appeal_rejected' ? t('Rejected') : t('Pending Review')}
                    </span>
                  </div>
                </div>
                <div className="card-body p-9">
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Submitted On')}</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">
                        {deactivation.appealed_at ? formatDateTime(deactivation.appealed_at) : '—'}
                      </span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Explanation')}</label>
                    <div className="col-lg-8">
                      <span className="fs-6 text-gray-800" style={{ whiteSpace: 'pre-wrap' }}>
                        {deactivation.appeal_text || '—'}
                      </span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Attachments')}</label>
                    <div className="col-lg-8">
                      {appealImages.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {appealImages.map((img, idx) => (
                            <a key={idx} href={img} target="_blank" rel="noreferrer" className="d-block">
                              <Image src={img} alt={`${t('Appeal attachment')} ${idx + 1}`} className="w-100px h-100px rounded object-fit-cover border border-gray-200" />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="fw-bold fs-6 text-gray-800">—</span>
                      )}
                    </div>
                  </div>

                  {!isAppealPending && (
                    <>
                      <div className="separator separator-dashed my-7"></div>
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Review Note')}</label>
                        <div className="col-lg-8">
                          <span className="fs-6 text-gray-800">{deactivation.review_note || '—'}</span>
                        </div>
                      </div>
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Reviewed By')}</label>
                        <div className="col-lg-8">
                          <span className="fw-bold fs-6 text-gray-800">
                            {deactivation.reviewed_by || '—'}
                            {deactivation.reviewed_at && (
                              <span className="text-muted fw-semibold fs-7 ms-2">
                                {t('on')} {formatDateTime(deactivation.reviewed_at)}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {isAppealPending && (
                    <div className="d-flex justify-content-end gap-2">
                      <button className="btn btn-light-danger" onClick={() => openAppealModal('reject')}>
                        <i className="fas fa-times me-2"></i>{t('Reject Appeal')}
                      </button>
                      <button className="btn btn-success" onClick={() => openAppealModal('approve')}>
                        <i className="fas fa-check me-2"></i>{t('Approve Appeal')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* User Header Card */}
            <div className="card mb-5 mb-xl-10">
              <div className="card-body pt-9 pb-0">
                <div className="d-flex flex-wrap flex-sm-nowrap">
                  <div className="me-7 mb-4">
                    <div className="symbol symbol-100px symbol-lg-160px symbol-fixed position-relative">
                      {user.picture ? (
                        <Image 
                          src={user.picture} 
                          alt={user.name} 
                          type="avatar"
                          className="w-100px h-100px rounded-3 object-fit-cover"
                        />
                      ) : (
                        <div className="symbol-label fs-1 fw-bold bg-light-primary text-primary">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className={`position-absolute translate-middle bottom-0 start-100 mb-6 bg-${user.accStatus === 1 ? 'success' : 'danger'} rounded-circle border border-4 border-body h-20px w-20px`}></div>
                    </div>
                  </div>

                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-center mb-2">
                          <span className="text-gray-900 text-hover-primary fs-2 fw-bold me-1">{user.name}</span>
                          {user.verification_badge === 'Green' && (
                             <span className="svg-icon svg-icon-1 svg-icon-primary">
                               <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                                 <path d="M10.0813 3.7242C10.8849 2.16438 13.1151 2.16438 13.9187 3.7242V3.7242C14.4016 4.66147 15.4909 5.1127 16.4951 4.79139V4.79139C18.1663 4.25668 19.7433 5.83365 19.2086 7.50485V7.50485C18.8873 8.50905 19.3385 9.59842 20.2758 10.0813V10.0813C21.8356 10.8849 21.8356 13.1151 20.2758 13.9187V13.9187C19.3385 14.4016 18.8873 15.491 19.2086 16.4951V16.4951C19.7433 18.1663 18.1663 19.7433 16.4951 19.2086V19.2086C15.491 18.8873 14.4016 19.3385 13.9187 20.2758V20.2758C13.1151 21.8356 10.8849 21.8356 10.0813 20.2758V20.2758C9.59842 19.3385 8.50905 18.8873 7.50485 19.2086V19.2086C5.83365 19.7433 4.25668 18.1663 4.79139 16.4951V16.4951C5.1127 15.491 4.66147 14.4016 3.7242 13.9187V13.9187C2.16438 13.1151 2.16438 10.8849 3.7242 10.0813V10.0813C4.66147 9.59842 5.1127 8.50905 4.79139 7.50485V7.50485C4.25668 5.83365 5.83365 4.25668 7.50485 4.79139V4.79139C8.50905 5.1127 9.59842 4.66147 10.0813 3.7242V3.7242Z" fill="currentColor"/>
                                 <path d="M14.8563 9.1903C15.0606 8.94984 15.3771 8.9385 15.6175 9.14289C15.858 9.34728 15.8229 9.66433 15.6185 9.9048L11.863 14.6558C11.6554 14.9001 11.2876 14.9258 11.048 14.7128L8.47656 12.4271C8.24068 12.2174 8.21944 11.8563 8.42911 11.6204C8.63877 11.3845 8.99996 11.3633 9.23583 11.5729L11.3706 13.4705L14.8563 9.1903Z" fill="white"/>
                               </svg>
                             </span>
                          )}
                          {user.verification_badge === 'Gold' && (
                             <span className="svg-icon svg-icon-1 svg-icon-warning">
                               <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                                 <path d="M10.0813 3.7242C10.8849 2.16438 13.1151 2.16438 13.9187 3.7242V3.7242C14.4016 4.66147 15.4909 5.1127 16.4951 4.79139V4.79139C18.1663 4.25668 19.7433 5.83365 19.2086 7.50485V7.50485C18.8873 8.50905 19.3385 9.59842 20.2758 10.0813V10.0813C21.8356 10.8849 21.8356 13.1151 20.2758 13.9187V13.9187C19.3385 14.4016 18.8873 15.491 19.2086 16.4951V16.4951C19.7433 18.1663 18.1663 19.7433 16.4951 19.2086V19.2086C15.491 18.8873 14.4016 19.3385 13.9187 20.2758V20.2758C13.1151 21.8356 10.8849 21.8356 10.0813 20.2758V20.2758C9.59842 19.3385 8.50905 18.8873 7.50485 19.2086V19.2086C5.83365 19.7433 4.25668 18.1663 4.79139 16.4951V16.4951C5.1127 15.491 4.66147 14.4016 3.7242 13.9187V13.9187C2.16438 13.1151 2.16438 10.8849 3.7242 10.0813V10.0813C4.66147 9.59842 5.1127 8.50905 4.79139 7.50485V7.50485C4.25668 5.83365 5.83365 4.25668 7.50485 4.79139V4.79139C8.50905 5.1127 9.59842 4.66147 10.0813 3.7242V3.7242Z" fill="currentColor"/>
                                 <path d="M14.8563 9.1903C15.0606 8.94984 15.3771 8.9385 15.6175 9.14289C15.858 9.34728 15.8229 9.66433 15.6185 9.9048L11.863 14.6558C11.6554 14.9001 11.2876 14.9258 11.048 14.7128L8.47656 12.4271C8.24068 12.2174 8.21944 11.8563 8.42911 11.6204C8.63877 11.3845 8.99996 11.3633 9.23583 11.5729L11.3706 13.4705L14.8563 9.1903Z" fill="white"/>
                               </svg>
                             </span>
                          )}
                        </div>

                        <div className="d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2">
                          <span className="d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-2">
                            <i className="fas fa-envelope fs-4 me-1"></i>
                            {user.email}
                          </span>
                          <span className="d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-2">
                            <i className="fas fa-map-marker-alt fs-4 me-1"></i>
                            {(lang === 'ar' ? user?.city?.name_ar : user?.city?.name) || user?.city?.name || '--'}, {(lang === 'ar' ? user?.country?.name_ar : user?.country?.name) || user?.country?.name || '--'}
                          </span>
                          <span className="d-flex align-items-center text-gray-400 text-hover-primary mb-2">
                            <i className="fas fa-phone fs-4 me-1"></i>
                            {user.phone || '--'}
                          </span>
                        </div>

                          <span className="d-flex align-items-center text-gray-400 text-hover-primary mb-2">
                            <i className="fas fa-hashtag fs-4 me-1"></i>
                            {user.fuid || '--'}
                          </span>

                      </div>

                      <div className="d-flex flex-column gap-3 my-4">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {user.verification_badge?.startsWith('Rejected') ? (
                            <div className="badge badge-light-danger fs-7 fw-bold">
                              {user.verification_badge ? t(user.verification_badge) : t('Unverified')}
                            </div>
                          ) : (
                            <div className={`badge badge-light-${user.verification_badge?.startsWith('Requested') ? 'warning' : (user.verification_badge !== 'Unverified' ? 'success' : 'secondary text-dark')} fs-7 fw-bold`}>
                              {user.verification_badge ? t(user.verification_badge) : t('Unverified')}
                            </div>
                          )}

                          {user.verification_badge?.startsWith('Requested') && (
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => openVerificationModal('approve')}
                              >
                                {t('Approve')}
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => openVerificationModal('reject')}
                              >
                                {t('Reject')}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Badge note */}
                        {user.badge_node && (
                          <div className="d-flex align-items-start gap-2">
                            <i className="bi bi-chat-left-text-fill text-muted mt-1"></i>
                            <div>
                              <div className="fw-semibold text-gray-700 fs-7 mb-1">{t('Admin Note')}</div>
                              <span className="text-gray-600 fs-7">{user.badge_node}</span>
                            </div>
                          </div>
                        )}

                        {/* Badge documents */}
                        {user.badge_documents && (() => {
                          const docs: string[] = typeof user.badge_documents === 'string'
                            ? JSON.parse(user.badge_documents)
                            : user.badge_documents;
                          return docs.length > 0 ? (
                            <div className="d-flex align-items-start gap-2">
                              <i className="bi bi-paperclip text-muted mt-1"></i>
                              <div>
                                <div className="fw-semibold text-gray-700 fs-7 mb-1">{t('Documents')}</div>
                                <div className="d-flex flex-wrap gap-2">
                                  {docs.map((doc: string, idx: number) => {
                                    const fileName = doc.split('/').pop() || doc;
                                    const ext = fileName.split('.').pop()?.toLowerCase();
                                    const isImage = ['jpg', 'jpeg', 'png'].includes(ext || '');
                                    
                                    return (
                                      <a
                                        key={idx}
                                        href={doc}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-sm btn-light-primary py-1 px-3 fs-7"
                                      >
                                        <i className={`bi ${isImage ? 'bi-image' : 'bi-file-earmark'} me-1`}></i>
                                        {fileName}
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <ul className="nav nav-tabs nav-pills border-0 flex-row d-flex fs-6 fw-semibold my-3 gap-2">
                  {([
                    { key: 'info', label: t('User Info'), icon: 'bi-person-lines-fill' },
                    { key: 'ads', label: t('User Ads'), icon: 'bi-megaphone-fill' },
                    { key: 'violations', label: t('Violations'), icon: 'bi-exclamation-triangle-fill' },
                    { key: 'reports', label: t('Reports'), icon: 'bi-flag-fill' },
                  ] as const).map((tab) => (
                    <li className="nav-item" key={tab.key}>
                      <button
                        className={`nav-link d-flex align-items-center px-5 py-3 rounded-pill border ${
                          activeTab === tab.key
                            ? 'active bg-primary text-white border-primary'
                            : 'bg-light text-gray-700 border-gray-200'
                        }`}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        <i className={`bi ${tab.icon} fs-5 me-2 ${activeTab === tab.key ? 'text-white' : 'text-primary'}`}></i>
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'info' && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">{t('Profile Details')}</h3>
                  </div>
                </div>
                <div className="card-body p-9">
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Full Name')}</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{user.name}</span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Username')}</label>
                    <div className="col-lg-8">
                      {user.username ? (
                        <span className="fw-bold fs-6 text-gray-800">@{user.username}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Email')}</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{user.email}</span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Contact Phone')}</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{user.phone || t('N/A')}</span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Account Type')}</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{(lang === 'ar' ? user?.account_type?.title_ar : user?.account_type?.title) || user?.account_type?.title || '--'}</span>
                    </div>
                  </div>

                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Location')}</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{(lang === 'ar' ? user?.city?.name_ar : user?.city?.name) || user?.city?.name || '--'}, {(lang === 'ar' ? user?.country?.name_ar : user?.country?.name) || user?.country?.name || '--'}</span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Account Status')}</label>
                    <div className="col-lg-8 d-flex align-items-center gap-3 flex-wrap">
                      <span className={`badge badge-light-${user.accStatus === 1 ? 'success' : 'danger'}`}>
                        {user.accStatus === 1 ? t('Active') : t('Deactivated')}
                      </span>
                      <button
                        className={`btn btn-sm ${user.accStatus === 1 ? 'btn-light-danger' : 'btn-light-success'}`}
                        onClick={openStatusModal}
                      >
                        <i className={`fas ${user.accStatus === 1 ? 'fa-user-slash' : 'fa-user-check'} me-2`}></i>
                        {user.accStatus === 1 ? t('Deactivate Account') : t('Reactivate Account')}
                      </button>
                    </div>
                  </div>
                  {user.accStatus !== 1 && (
                    <>
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Deactivation Reason')}</label>
                        <div className="col-lg-8">
                          <span className="fw-bold fs-6 text-gray-800">{user.deactivation?.reason || '—'}</span>
                        </div>
                      </div>
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Deactivated On')}</label>
                        <div className="col-lg-8">
                          <span className="fw-bold fs-6 text-gray-800">
                            {user.deactivation?.deactivated_at ? formatDateTime(user.deactivation.deactivated_at) : '—'}
                          </span>
                        </div>
                      </div>
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Deactivated By')}</label>
                        <div className="col-lg-8">
                          <span className="fw-bold fs-6 text-gray-800">{user.deactivation?.deactivated_by || '—'}</span>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Classification')}</label>
                    <div className="col-lg-8">
                      <span className="badge badge-light-info fs-7 fw-bold text-capitalize">
                        {user.user_type ? t(user.user_type.replace(/_/g, ' ')) : t('N/A')}
                      </span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Signin Method')}</label>
                    <div className="col-lg-8">
                      {user.googleId ? (
                        <span className="badge badge-light-danger fs-7 fw-bold"><i className="bi bi-google me-1"></i>{t('Google')}</span>
                      ) : user.appleId ? (
                        <span className="badge badge-light-dark fs-7 fw-bold"><i className="bi bi-apple me-1"></i>{t('Apple')}</span>
                      ) : (
                        <span className="badge badge-light-primary fs-7 fw-bold"><i className="bi bi-envelope-fill me-1"></i>{t('Email')}</span>
                      )}
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Verification')}</label>
                    <div className="col-lg-8">
                      <span className={`badge fs-7 fw-bold ${
                        user.verification_badge?.includes('Green') ? 'badge-light-success' :
                        user.verification_badge?.includes('Gold') ? 'badge-light-warning' :
                        user.verification_badge?.startsWith('Requested') ? 'badge-light-warning' :
                        user.verification_badge?.startsWith('Rejected') ? 'badge-light-danger' :
                        'badge-light-secondary text-dark'
                      }`}>
                        {user.verification_badge ? t(user.verification_badge) : t('Unverified')}
                      </span>
                    </div>
                  </div>
                  {user.badge_node && (
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Badge Note')}</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{user.badge_node}</span>
                      </div>
                    </div>
                  )}
                  {user.badge_documents && (() => {
                    const docs: string[] = typeof user.badge_documents === 'string'
                      ? JSON.parse(user.badge_documents)
                      : user.badge_documents;
                    return docs.length > 0 ? (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Badge Documents')}</label>
                        <div className="col-lg-8 d-flex flex-wrap gap-2">
                          {docs.map((doc: string, idx: number) => {
                            const fileName = doc.split('/').pop() || doc;
                            const ext = fileName.split('.').pop()?.toLowerCase();
                            const isImage = ['jpg', 'jpeg', 'png'].includes(ext || '');

                            return (
                              <a key={idx} href={doc} target="_blank" rel="noreferrer" className="btn btn-sm btn-light-primary">
                                <i className={`bi ${isImage ? 'bi-image' : 'bi-file-earmark'} me-1`}></i>
                                {fileName}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    ) : null;
                  })()}
                   <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">{t('Joined Date')}</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{formatDateTime(user.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Wallet Card — shown inside info tab */}
            {activeTab === 'info' && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">{t('Wallet Balance')}</h3>
                  </div>
                </div>
                <div className="card-body p-9">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-4">
                    <div>
                      <p className="text-muted fw-semibold mb-1">{t('Current Balance')}</p>
                      <span className="fs-2x fw-bold text-gray-800">
                        {user.wallet_balance ?? 0}
                      </span>
                    </div>
                    <div className="d-flex gap-3">
                      <button
                        className="btn btn-success"
                        onClick={() => openWalletModal('add')}
                      >
                        <i className="fas fa-plus me-2"></i>{t('Add Balance')}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => openWalletModal('deduct')}
                        disabled={(user.wallet_balance ?? 0) <= 0}
                      >
                        <i className="fas fa-minus me-2"></i>{t('Deduct Balance')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'violations' && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">{t('Violations')}</h3>
                  </div>
                  <div className="card-toolbar">
                    <button className="btn btn-danger btn-sm" onClick={openViolationModal}>
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>{t('Give Violation')}
                    </button>
                  </div>
                </div>
                <div className="card-body p-9">
                  {isViolationsLoading ? (
                    <PageLoader />
                  ) : violationsData?.data && violationsData.data.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed fs-6 gy-5">
                        <thead>
                          <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>{t('TYPE OF VIOLATION')}</th>
                            <th>{t('AFFECTED CONTENT')}</th>
                            <th>{t('DATE')}</th>
                            <th>{t('STATUS')}</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {violationsData.data.map((v: any) => (
                            <tr key={v.id}>
                              <td>{v.violation_type ? t(v.violation_type) : t('Policy Non-Compliance')}</td>
                              <td>
                                {v.ad?.title
                                  ? `${v.ad.title} / ${(lang === 'ar' ? v.ad.ad_category?.title_ar : v.ad.ad_category?.title) || v.ad.ad_category?.title || ''}`
                                  : v.ad_id}
                              </td>
                              <td>{formatDateOnly(v.created_at)}</td>
                              <td>
                                <span className={`badge ${
                                  v.status === 'Restricted' ? 'badge-light-danger' :
                                  v.status === 'Warning Issued' ? 'badge-light-warning' :
                                  'badge-light-info'
                                }`}>
                                  {t(v.status)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-10">{t('No violations found for this user.')}</div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              <>
                <ReportsSection
                  title={t('Reports Against This User')}
                  emptyText={t('No reports have been filed against this user.')}
                  reports={reportsData?.data?.user_reports}
                  isLoading={isReportsLoading}
                />
                <ReportsSection
                  title={t("Ad Reports Against This User's Ads")}
                  emptyText={t("No reports have been filed against this user's ads.")}
                  reports={reportsData?.data?.ad_reports_against}
                  isLoading={isReportsLoading}
                  onOpenAd={(adId) => navigate(`/admin/ads/${adId}`)}
                />
                <ReportsSection
                  title={t('Ad Reports Filed By This User')}
                  emptyText={t('This user has not reported any ads.')}
                  reports={reportsData?.data?.ad_reports_by}
                  isLoading={isReportsLoading}
                  onOpenAd={(adId) => navigate(`/admin/ads/${adId}`)}
                />
              </>
            )}

            {activeTab === 'ads' && (
              <div className="card mb-5 mb-xl-10">
                 <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">{t('User Ads')}</h3>
                  </div>
                </div>
                <div className="card-body p-9">
                  {isAdsLoading ? (
                    <PageLoader />
                  ) : adsData?.data?.data && adsData.data.data.length > 0 ? (
                    <div className="table-responsive">
                       <table className="table align-middle table-row-dashed fs-6 gy-5">
                        <thead>
                          <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>{t('AD ID')}</th>
                            <th>{t('TITLE')}</th>
                            <th>{t('CATEGORY')}</th>
                            <th>{t('TYPE')}</th>
                            <th>{t('LANGUAGE')}</th>
                            <th>{t('STATUS')}</th>
                            <th>{t('VIEWS')}</th>
                            <th>{t('CLICKS')}</th>
                            <th>{t('PAID AMOUNT')}</th>
                            <th>{t('CREATED DATE')}</th>
                            <th>{t('ACTIONS')}</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {adsData.data.data.map((ad: any) => (
                            <tr key={ad.id}>
                              <td>
                                <span className="text-gray-800 fw-bold">{ad.ad_id || ad.id}</span>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <span className="text-gray-800 fw-bold">{ad.title || t('N/A')}</span>
                                  {ad.description && (
                                    <span className="text-muted fs-7" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {ad.description}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="badge badge-light-primary">
                                  {(lang === 'ar' ? ad.ad_category?.title_ar : ad.ad_category?.title) || ad.ad_category?.title || t('N/A')}
                                </span>
                              </td>
                              <td>
                                <span className="badge badge-light-info">
                                  {(lang === 'ar' ? ad.ad_type?.title_ar : ad.ad_type?.title) || ad.ad_type?.title || t('N/A')}
                                </span>
                              </td>

                              <td>
                                <span className="badge badge-light-success">
                                  {ad.ad_language?.toUpperCase() || 'EN'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${getStatusColor(ad.status)}`}>
                                  {ad.status ? t(ad.status) : t('N/A')}
                                </span>
                              </td>
                              <td>
                                <span className="text-gray-800 fw-bold">
                                  {ad.ad_stat?.views ?? ad.views ?? 0}
                                </span>
                              </td>
                              <td>
                                <span className="text-gray-800 fw-bold">
                                  {ad.ad_stat?.clicks ?? ad.clicks ?? 0}
                                </span>
                              </td>
                              <td>
                                <span className="text-gray-800 fw-bold">
                                  {formatCurrency(ad.payment_amount)}
                                </span>
                              </td>
                              <td>{formatDateTime(ad.created_at)}</td>
                              <td className="text-center">
                                <ActionButtons
                                  onView={() => navigate(`/admin/ads/${ad.id}`)}
                                  // onDelete={() => handleDelete(ad.id)}
                                  viewTitle={t('View Details')}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                       </table>
                      <div className="mt-5">
                        <Pagination
                          currentPage={adsData.data.current_page}
                          lastPage={adsData.data.last_page}
                          total={adsData.data.total}
                          perPage={adsData.data.per_page || 15}
                          onPageChange={(newPage) => setAdsPage(newPage)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-muted">{t('No ads found for this user.')}</div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>

    {/* Verification Modal */}
    {modalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {pendingStatus === 'approve' ? t('Approve Badge') : t('Reject Badge')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalOpen(false)}
                  disabled={isUpdating}
                />
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <label className="form-label fw-semibold required">{t('Note')}</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder={pendingStatus === 'approve' ? t('Add a note for this approval...') : t('Add a note for this rejection...')}
                    value={node}
                    onChange={(e) => setNode(e.target.value)}
                  />
                </div>

                {pendingStatus === 'approve' && (
                  <div className="mb-2">
                    <label className="form-label fw-semibold">{t('Upload Documents')}</label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      onChange={(e) => setDocuments(e.target.files ? Array.from(e.target.files) : [])}
                    />
                    {documents.length > 0 && (
                      <div className="mt-2">
                        {documents.map((f, i) => (
                          <span key={i} className="badge badge-light-primary me-1 mb-1">{f.name}</span>
                        ))}
                      </div>
                    )}
                    <div className="text-muted fs-7 mt-1">{t('Accepted: JPG, PNG, PDF, DOC, DOCX (max 5MB each)')}</div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setModalOpen(false)}
                  disabled={isUpdating}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className={`btn ${pendingStatus === 'approve' ? 'btn-success' : 'btn-danger'}`}
                  onClick={handleVerificationSubmit}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <span className="indicator-progress d-block">
                      {t('Please wait...')} <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  ) : pendingStatus === 'approve' ? t('Approve') : t('Reject')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isUpdating && setModalOpen(false)} />
      </>
    )}

    {/* Wallet Modal */}
    {walletModalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {walletType === 'add' ? t('Add Wallet Balance') : t('Deduct Wallet Balance')}
                  <span className="text-muted fs-6 ms-2">— {user.name}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setWalletModalOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="d-flex gap-3 mb-5">
                  <button
                    type="button"
                    className={`btn flex-grow-1 ${walletType === 'add' ? 'btn-success' : 'btn-light'}`}
                    onClick={() => setWalletType('add')}
                  >
                    <i className="fas fa-plus me-2"></i>{t('Add')}
                  </button>
                  <button
                    type="button"
                    className={`btn flex-grow-1 ${walletType === 'deduct' ? 'btn-danger' : 'btn-light'}`}
                    onClick={() => setWalletType('deduct')}
                  >
                    <i className="fas fa-minus me-2"></i>{t('Deduct')}
                  </button>
                </div>
                <div className="mb-1 p-4 bg-light rounded mb-4">
                  <span className="text-muted fs-7">{t('Current Balance')}</span>
                  <div className="fw-bold fs-4 text-gray-800">{user.wallet_balance ?? 0}</div>
                </div>
                <div className="mb-4">
                  <label className="form-label required">{t('Amount')}</label>
                  <input
                    type="number"
                    className="form-control form-control-solid"
                    placeholder={t('Enter amount')}
                    min="0.01"
                    step="0.01"
                    value={walletAmount}
                    onChange={(e) => setWalletAmount(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">{t('Note (optional)')}</label>
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    placeholder={t('e.g. Refund, penalty, bonus…')}
                    value={walletNote}
                    onChange={(e) => setWalletNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setWalletModalOpen(false)}>
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className={`btn ${walletType === 'add' ? 'btn-success' : 'btn-danger'}`}
                  onClick={handleWalletSubmit}
                  disabled={isWalletLoading || !walletAmount}
                >
                  {isWalletLoading ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>{t('Processing…')}</>
                  ) : walletType === 'add' ? t('Add Balance') : t('Deduct Balance')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isWalletLoading && setWalletModalOpen(false)} />
      </>
    )}

    {/* Account Deactivation Modal */}
    {statusModalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {user.accStatus === 1 ? t('Deactivate Account') : t('Reactivate Account')}
                  <span className="text-muted fs-6 ms-2">— {user.name}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setStatusModalOpen(false)} disabled={isStatusUpdating} />
              </div>
              <div className="modal-body">
                {user.accStatus === 1 ? (
                  <>
                    <div className="alert alert-warning d-flex align-items-start gap-3">
                      <i className="fas fa-exclamation-triangle fs-3 text-warning"></i>
                      <span className="fs-7">
                        {t('The user will be signed out of the platform and blocked from signing in again. They will be notified with the reason you enter below.')}
                      </span>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fw-semibold required">{t('Reason for deactivation')}</label>
                      <textarea
                        className="form-control form-control-solid"
                        rows={4}
                        maxLength={191}
                        placeholder={t('Describe why this account is being deactivated…')}
                        value={deactivationReason}
                        onChange={(e) => setDeactivationReason(e.target.value)}
                        autoFocus
                      />
                      <div className="text-muted fs-8 mt-1">{deactivationReason.length}/191</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="alert alert-info d-flex align-items-start gap-3">
                      <i className="fas fa-info-circle fs-3 text-info"></i>
                      <span className="fs-7">
                        {t('The user will regain access to the platform and be notified that their account is active again.')}
                      </span>
                    </div>
                    {user.deactivation?.reason && (
                      <div className="p-4 bg-light rounded">
                        <span className="text-muted fs-7 d-block">{t('Deactivated for')}</span>
                        <div className="fw-bold fs-6 text-gray-800">{user.deactivation?.reason}</div>
                        {user.deactivation?.deactivated_at && (
                          <span className="text-muted fs-8">
                            {formatDateTime(user.deactivation?.deactivated_at)}
                            {user.deactivation?.deactivated_by ? ` — ${t('by')} ${user.deactivation?.deactivated_by}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setStatusModalOpen(false)} disabled={isStatusUpdating}>
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className={`btn ${user.accStatus === 1 ? 'btn-danger' : 'btn-success'}`}
                  onClick={handleStatusSubmit}
                  disabled={isStatusUpdating || (user.accStatus === 1 && !deactivationReason.trim())}
                >
                  {isStatusUpdating ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>{t('Processing…')}</>
                  ) : user.accStatus === 1 ? t('Deactivate Account') : t('Reactivate Account')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isStatusUpdating && setStatusModalOpen(false)} />
      </>
    )}

    {/* Appeal Review Modal */}
    {appealModalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {appealAction === 'approve' ? t('Approve Appeal') : t('Reject Appeal')}
                  <span className="text-muted fs-6 ms-2">— {user.name}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setAppealModalOpen(false)} disabled={isAppealUpdating} />
              </div>
              <div className="modal-body">
                {appealAction === 'approve' ? (
                  <div className="alert alert-info d-flex align-items-start gap-3">
                    <i className="fas fa-info-circle fs-3 text-info"></i>
                    <span className="fs-7">
                      {t('The account will be reactivated immediately and the user will be notified that they can sign in again.')}
                    </span>
                  </div>
                ) : (
                  <div className="alert alert-warning d-flex align-items-start gap-3">
                    <i className="fas fa-exclamation-triangle fs-3 text-warning"></i>
                    <span className="fs-7">
                      {t('The account stays deactivated. The user will be notified with the reason you enter below.')}
                    </span>
                  </div>
                )}

                {deactivation.appeal_text && (
                  <div className="p-4 bg-light rounded mb-4">
                    <span className="text-muted fs-7 d-block mb-1">{t("User's explanation")}</span>
                    <div className="fs-6 text-gray-800" style={{ whiteSpace: 'pre-wrap' }}>{deactivation.appeal_text}</div>
                  </div>
                )}

                <div className="mb-2">
                  <label className={`form-label fw-semibold ${appealAction === 'reject' ? 'required' : ''}`}>
                    {appealAction === 'approve' ? t('Note (optional)') : t('Reason for rejection')}
                  </label>
                  <textarea
                    className="form-control form-control-solid"
                    rows={4}
                    maxLength={500}
                    placeholder={appealAction === 'approve'
                      ? t('Add an internal note about this decision…')
                      : t('Explain why this appeal is being rejected…')}
                    value={appealNote}
                    onChange={(e) => setAppealNote(e.target.value)}
                    autoFocus
                  />
                  <div className="text-muted fs-8 mt-1">{appealNote.length}/500</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setAppealModalOpen(false)} disabled={isAppealUpdating}>
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className={`btn ${appealAction === 'approve' ? 'btn-success' : 'btn-danger'}`}
                  onClick={handleAppealSubmit}
                  disabled={isAppealUpdating || (appealAction === 'reject' && !appealNote.trim())}
                >
                  {isAppealUpdating ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>{t('Processing…')}</>
                  ) : appealAction === 'approve' ? t('Approve Appeal') : t('Reject Appeal')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isAppealUpdating && setAppealModalOpen(false)} />
      </>
    )}

    {/* Violation Modal */}
    {violationModalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('Give Violation')}<span className="text-muted fs-6 ms-2">— {user.name}</span></h5>
                <button type="button" className="btn-close" onClick={() => setViolationModalOpen(false)} disabled={isViolationSubmitting} />
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <label className="form-label fw-semibold required">{t('Affected Ad')}</label>
                  <select
                    className="form-select form-select-solid"
                    value={violationAdId}
                    onChange={(e) => setViolationAdId(e.target.value)}
                  >
                    <option value="">{t('Select an ad…')}</option>
                    {adsData?.data?.data?.map((ad: any) => (
                      <option key={ad.id} value={ad.id}>
                        {ad.title || `${t('Ad')} #${ad.ad_id || ad.id}`} {ad.ad_category?.title ? `/ ${(lang === 'ar' ? ad.ad_category.title_ar : ad.ad_category.title) || ad.ad_category.title}` : ''}
                      </option>
                    ))}
                  </select>
                  {(!adsData?.data?.data || adsData.data.data.length === 0) && (
                    <div className="text-muted fs-7 mt-1">{t('No ads found. Switch to the Ads tab to load them first.')}</div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold required">{t('Type of Violation')}</label>
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    value={violationType}
                    onChange={(e) => setViolationType(e.target.value)}
                    placeholder={t('e.g. Policy Non-Compliance')}
                  />
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold required">{t('Status')}</label>
                  <select
                    className="form-select form-select-solid"
                    value={violationStatus}
                    onChange={(e) => setViolationStatus(e.target.value)}
                  >
                    <option value="Under Review">{t('Under Review')}</option>
                    <option value="Warning Issued">{t('Warning Issued')}</option>
                    <option value="Restricted">{t('Restricted')}</option>
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">{t('Note (optional)')}</label>
                  <textarea
                    className="form-control form-control-solid"
                    rows={3}
                    placeholder={t('Additional details about this violation…')}
                    value={violationNote}
                    onChange={(e) => setViolationNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setViolationModalOpen(false)} disabled={isViolationSubmitting}>
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleViolationSubmit}
                  disabled={isViolationSubmitting || !violationAdId}
                >
                  {isViolationSubmitting ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>{t('Submitting…')}</>
                  ) : t('Give Violation')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isViolationSubmitting && setViolationModalOpen(false)} />
      </>
    )}
    </>
  );
};

export default AdminUserView;


